/**
 * Punto de entrada de la app React. Incluido desde `src/index.html`.
 *
 * Arranca en tres pasos, y el orden importa:
 *
 * 1. `/config.json` — la configuración es de runtime (nginx la genera por
 *    entorno con `envsubst`), así que sin ella no se sabe ni a qué realm de
 *    Keycloak hablar.
 * 2. `initKeycloak()` — la sesión se resuelve ANTES de montar React
 *    (KEYCLOAK_SSO_SETUP.md §2.5). Si se resolviera después, la app alcanzaría
 *    a renderizar una vista sin autenticar. En la práctica rara vez retorna sin
 *    sesión: cuando no hay ninguna, el navegador se va al portal de apps o al
 *    login nativo y esta página deja de existir.
 * 3. Montar `<App />`, ya con token válido y rol verificado.
 */

import { createRoot } from "react-dom/client";
import type { ReactNode } from "react";
import { App } from "./App";
import { initKeycloak } from "./config/keycloak.config";
import type { ErrorSso } from "./config/keycloak.config";
import { PantallaErrorSso, PantallaSinAcceso, PantallaVerificando } from "./auth/PantallaSso";
import { puedeIngresar } from "./auth/usuario";

const root = createRoot(document.getElementById("root")!);

function esErrorSso(valor: unknown): valor is ErrorSso {
  return typeof valor === "object" && valor !== null && "titulo" in valor && "detalle" in valor;
}

function render(node: ReactNode) {
  root.render(node);
}

async function cargarConfiguracion(): Promise<void> {
  const res = await fetch("/config.json");
  (window as any).__env__ = await res.json();
}

async function arrancar() {
  render(<PantallaVerificando />);

  try {
    await cargarConfiguracion();
    await initKeycloak();
  } catch (error) {
    render(
      <PantallaErrorSso
        error={
          esErrorSso(error)
            ? error
            : {
                titulo: "No fue posible iniciar sesión",
                detalle: "Ocurrió un error inesperado al validar su sesión institucional.",
              }
        }
      />,
    );
    return;
  }

  // Autenticado en el realm pero sin el rol de esta app: montar `<App />`
  // dejaría una interfaz que responde 403 a cada acción, que se lee como
  // aplicación rota en vez de como permiso faltante.
  if (!puedeIngresar()) {
    render(<PantallaSinAcceso />);
    return;
  }

  render(<App />);
}

void arrancar();
